import './SchedulerLayout.scss';

import * as formik from 'formik';
import * as yup from 'yup';

import { faImage, faLink, faPalette, faUpload } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';

import AppButton from '../../../commons/buttons/AppButton';
import AppInput from '../../../commons/inputs/AppInput';
import AppLinkButton from '../../../commons/buttons/AppLinkButton';
import AppLoading from '../../../commons/AppLoading';
import CompanyAPI from '../../company/CompanyAPI';
import FloatingVideoButton from '../../../commons/buttons/FloatingVideoButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form } from 'react-bootstrap';
import HallAppCenterContainer from '../../../commons/containers/HallAppCenterContainer';
import HallAppConst from '../../../commons/util/HallAppConst';
import HallAppToast from '../../../commons/HallAppToast';
import ModalSchedulerLayoutColor from '../components/ModalShedulerLayoutColor';
import SchedulerConfigAPI from '../SchedulerConfigAPI';
import { compressImage } from '../../../commons/util/HallAppUtil';
import noImg from '../../../assets/images/no-img.jpg';

function SchedulerLayoutConfig() {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  const [backgroundType, setBackgroundType] = useState('COLOR'); // 'COLOR' | 'IMG'
  const [showModalColor, setShowModalColor] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#D8BFD8');
  const [imgBackground, setImgBackground] = useState();
  const [imgLogo, setImgLogo] = useState();
  const [layout, setLayout] = useState();
  const [principalColor, setPrincipalColor] = useState('#D8BFD8');
  const [showModalPrincipalColor, setShowModalPrincipalColor] = useState(false);

  const schedulerAPI = new SchedulerConfigAPI();
  const companyAPI = new CompanyAPI();

  const [initialValues, setInitialValues] = useState({
    nickname: '',
    principalColor: '',
    slotIntervalMinutes: 60,

    // ✅ NOVO: modo de criação de comanda
    // AUTOMATIC: cria comanda ao criar agendamento e contempla exclusão/cancelamento
    // MANUAL: não cria comanda automaticamente
    comandaMode: 'AUTOMATIC',
  });

  const { Formik } = formik;

  const schema = yup.object().shape({
    nickname: yup
      .string()
      .max(30, 'Nome deve ter no máximo 30 caracteres')
      .matches(/^[a-z0-9._]+$/, 'Use apenas letras minúsculas, números, ponto e underline, sem espaços'),
    principalColor: yup.string(),
    slotIntervalMinutes: yup
      .number()
      .oneOf([15, 30, 60, 120], 'Selecione um intervalo válido')
      .required('Intervalo é obrigatório'),

    // ✅ NOVO
    comandaMode: yup
      .string()
      .oneOf(['AUTOMATIC', 'MANUAL'], 'Selecione uma opção válida')
      .required('Selecione como deseja criar as comandas'),
  });

  const save = async (values, { setErrors }) => {
    const data = {
      ...values,
      principalColor,
      backgroundType,
      imgBackground: backgroundType === 'COLOR' ? null : imgBackground,
      imgLogo,
      companyId: companyAPI.getCompanyManagment().id,
      backgroundColor,
      slotIntervalMinutes: Number(values.slotIntervalMinutes),

      // ✅ NOVO
      comandaMode: values.comandaMode,
    };

    const formData = new FormData();
    formData.append('dto', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    setIsLoadingButton(true);

    if (imgBackground) {
      const backgroundCompress = await compressImage(imgBackground);
      formData.append('imgBackground', backgroundCompress);
    }

    if (imgLogo) {
      const logoCompress = await compressImage(imgLogo);
      formData.append('imgLogo', logoCompress);
    }

    try {
      if (layout?.id) {
        await schedulerAPI.updateLayout(layout.id, formData);
      } else {
        await schedulerAPI.createLayout(formData);
        await getLayout();
      }
      HallAppToast.success();
    } catch (error) {
      const response = error.response;
      if (response?.status === 400) {
        HallAppToast.error(HallAppConst.ERROR_IN_FORM);
        if (response?.data?.[0]?.code === 'NICKNAME_ALREADY_EXISTS') {
          setErrors({ nickname: 'O nome informado já existe.' });
        }
      } else {
        HallAppToast.error(HallAppConst.GENERIC_ERROR);
      }
    } finally {
      setIsLoadingButton(false);
    }
  };

  async function getLayout() {
    try {
      setIsLoading(true);
      const resp = await schedulerAPI.getLayout();
      setLayout(resp.data);
      populateDataLayout(resp.data);
    } catch (_) {
      setLayout(null);
    } finally {
      setIsLoading(false);
    }
  }

  function populateDataLayout(data) {
    setBackgroundType(data.layoutBackgroundType);

    if (data.layoutBackgroundType === 'COLOR') {
      setBackgroundColor(data.value);
    }

    setInitialValues({
      nickname: data.nickname,
      principalColor: data.principalColor,
      slotIntervalMinutes: data.slotIntervalMinutes ?? 60,

      // ✅ NOVO
      comandaMode: data.comandaMode ?? 'MANUAL',
    });

    setPrincipalColor(data.principalColor);
  }

  function intervalLabel(minutes) {
    const m = Number(minutes);
    if (m === 15) return 'A cada 15 minutos';
    if (m === 30) return 'A cada 30 minutos';
    if (m === 60) return 'A cada 1 hora';
    if (m === 120) return 'A cada 2 horas';
    return `${m} min`;
  }

  // só pra prévia visual no config (não precisa ser perfeito)
  function buildPreviewTimes(minutes) {
    const m = Number(minutes);
    // exemplo: começa em 10:00 e mostra alguns horários
    const startHour = 10;
    const startMinute = 0;
    const count = 8;

    const pad = (n) => String(n).padStart(2, '0');

    let h = startHour;
    let min = startMinute;

    const out = [];
    for (let i = 0; i < count; i++) {
      out.push(`${pad(h)}:${pad(min)}`);
      min += m;
      while (min >= 60) {
        min -= 60;
        h += 1;
      }
    }
    return out;
  }

  useEffect(() => {
    getLayout();
  }, []);

  function changeColor(value) {
    setBackgroundType('COLOR');
    setImgBackground(undefined);
    setBackgroundColor(value);
  }

  function handleChangeImgBackground(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBackgroundType('IMG');
    setImgBackground(file);
  }

  function handleChangeImgLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLogo(file);
  }

  // ✅ NOVO: replicar a cor do fundo (quando for COLOR)
  function replicateBackgroundColorToPrincipal() {
    if (backgroundType !== 'COLOR') return;
    if (!backgroundColor) return;
    setPrincipalColor(backgroundColor);
    HallAppToast.success('Cor principal replicada do fundo!');
  }

  const backgroundPreview =
    backgroundType === 'COLOR'
      ? { backgroundColor }
      : {
        backgroundImage: `url(${(imgBackground && URL.createObjectURL(imgBackground)) || layout?.value || ''
          })`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };

  const logoSrc =
    (imgLogo && URL.createObjectURL(imgLogo)) ||
    (layout?.logo ? `${layout.logo}?t=${new Date().getTime()}` : noImg);

  return (
    <>
      {isLoading ? (
        <HallAppCenterContainer>
          <AppLoading />
        </HallAppCenterContainer>
      ) : (
        <>
          <div className="container">
            {/* Top Preview */}
            <div className="scheduler-layout__top" style={backgroundPreview}>
              <div className="scheduler-layout__actions">
                <button
                  type="button"
                  className="scheduler-layout__action-btn"
                  onClick={() => setShowModalColor(true)}
                  aria-label="Selecionar cor de fundo"
                >
                  <FontAwesomeIcon icon={faPalette} />
                  <span>Adicionar cor</span>
                </button>

                <label
                  htmlFor="file-upload-bg"
                  className="scheduler-layout__action-btn"
                  aria-label="Enviar imagem de fundo"
                >
                  <FontAwesomeIcon icon={faImage} />
                  <span>Adicionar imagem</span>
                </label>
                <input
                  id="file-upload-bg"
                  type="file"
                  accept="image/*"
                  onChange={handleChangeImgBackground}
                  className="visually-hidden"
                />
              </div>

              <div className="scheduler-layout__logo">
                <img src={logoSrc} alt="logotipo" />
              </div>
            </div>

            {/* Bottom Form */}
            <div className="scheduler-layout__bottom">
              <div className="scheduler-layout__logo-upload">
                <label
                  htmlFor="file-upload-logo"
                  className="scheduler-layout__action-btn scheduler-layout__action-btn--outline"
                  aria-label="Enviar logotipo"
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <span>Adicionar logo</span>
                </label>
                <input
                  id="file-upload-logo"
                  type="file"
                  accept="image/*"
                  onChange={handleChangeImgLogo}
                  className="visually-hidden"
                />
              </div>

              <div className="scheduler-layout__form">
                <Formik
                  validationSchema={schema}
                  onSubmit={save}
                  initialValues={initialValues}
                  enableReinitialize
                >
                  {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                      <div className="container">
                        <AppInput
                          errors={errors}
                          touched={touched}
                          readOnly
                          name="principalColor"
                          onChange={handleChange}
                          backgroundColor={principalColor}
                          onClick={() => setShowModalPrincipalColor(true)}
                          formText="Usada nos botões e destaques."
                          type="text"
                          label="Cor Principal"
                        />

                        {/* ✅ NOVO BOTÃO: replicar cor do fundo (apenas se fundo for COR) */}
                        {backgroundType === 'COLOR' && (
                          <button
                            type="button"
                            onClick={replicateBackgroundColorToPrincipal}
                            className="scheduler-layout__replicate-btn"
                          >
                            <span
                              className="scheduler-layout__replicate-dot"
                              style={{ backgroundColor }}
                            />
                            Usar a mesma cor do fundo
                          </button>
                        )}

                        <AppInput
                          errors={errors}
                          touched={touched}
                          name="nickname"
                          onChange={handleChange}
                          value={values.nickname}
                          formText={`Esse nome será usado no link público:\n${HallAppConst.AGENDAMENTO_URL}/${values.nickname || ''
                            }`}
                          type="text"
                          label="Nome para link de agendamento"
                        />

                        {/* ✅ NOVO: modo de criação de comandas */}
                        {/* <div className="scheduler-layout__interval">
                          <label className="scheduler-layout__interval-label">
                            Comandas no agendamento
                          </label>

                          <select
                            name="comandaMode"
                            value={values.comandaMode}
                            onChange={handleChange}
                            className={`scheduler-layout__interval-select ${touched.comandaMode && errors.comandaMode ? 'is-invalid' : ''
                              }`}
                          >
                            <option value="AUTOMATIC">Criar comandas automaticamente</option>
                            <option value="MANUAL">Criar comandas manualmente</option>
                          </select>

                          {touched.comandaMode && errors.comandaMode && (
                            <div className="scheduler-layout__interval-error">{errors.comandaMode}</div>
                          )}

                          <div className="scheduler-layout__interval-help">
                            {values.comandaMode === 'AUTOMATIC'
                              ? 'Ao criar ou excluir um agendamento, o sistema também cria/remove a comanda automaticamente.'
                              : 'Ao criar um agendamento, nenhuma comanda será criada. Você cria a comanda quando quiser.'}
                          </div>
                        </div> */}

                        <div className="scheduler-layout__interval">
                          <label className="scheduler-layout__interval-label">
                            Intervalo dos horários no agendamento
                          </label>

                          <select
                            name="slotIntervalMinutes"
                            value={values.slotIntervalMinutes}
                            onChange={handleChange}
                            className={`scheduler-layout__interval-select ${touched.slotIntervalMinutes && errors.slotIntervalMinutes ? 'is-invalid' : ''
                              }`}
                          >
                            <option value={15}>A cada 15 minutos</option>
                            <option value={30}>A cada 30 minutos</option>
                            <option value={60}>A cada 1 hora</option>
                            <option value={120}>A cada 2 horas</option>
                          </select>

                          {touched.slotIntervalMinutes && errors.slotIntervalMinutes && (
                            <div className="scheduler-layout__interval-error">
                              {errors.slotIntervalMinutes}
                            </div>
                          )}

                          <div className="scheduler-layout__interval-help">
                            Isso define de quanto em quanto tempo os horários aparecem no site.
                          </div>

                          {/* ✅ Prévia de como vai ficar */}
                          <div className="scheduler-layout__interval-preview">
                            <div className="scheduler-layout__interval-preview-title">
                              Prévia ({intervalLabel(values.slotIntervalMinutes)}):
                            </div>
                            <div className="scheduler-layout__interval-preview-chips">
                              {buildPreviewTimes(values.slotIntervalMinutes).map((t) => (
                                <span key={t} className="scheduler-layout__interval-chip">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {isLoadingButton ? (
                        <HallAppCenterContainer>
                          <AppLoading />
                        </HallAppCenterContainer>
                      ) : (
                        <AppButton width="100%" name="salvar" type="submit" />
                      )}

                      {layout?.id && (
                        <div className="scheduler-layout__share">
                          <AppLinkButton
                            width="100%"
                            name="compartilhar link"
                            onClick={() => {
                              const urlToShare = `${HallAppConst.AGENDAMENTO_URL}/${values.nickname}`;
                              const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(urlToShare)}`;
                              window.open(whatsappUrl, '_blank');
                            }}
                            leftIcon={<FontAwesomeIcon icon={faLink} />}
                          />
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>

          <FloatingVideoButton
            isSecondFloatingButton={false}
            youtubeUrl={'https://youtube.com/embed/XQPO2DWdV38'}
          />

          {/* Modais de cor */}
          <ModalSchedulerLayoutColor
            setBackgroundColor={changeColor}
            show={showModalColor}
            closeModal={() => setShowModalColor(false)}
          />

          <ModalSchedulerLayoutColor
            setBackgroundColor={(color) => setPrincipalColor(color)}
            show={showModalPrincipalColor}
            closeModal={() => setShowModalPrincipalColor(false)}
          />
        </>
      )}
    </>
  );
}

export default SchedulerLayoutConfig;
